import { createElement } from 'react';

import { MasterOutlet } from '@app/api/models/acb';
import { RequestPayload } from '@app/api/types';
import {
  GridColumn,
  GridDraft,
  GridFilter,
  GridLookup,
  GridView,
} from '@app/components/common/editable-grid/model';

import {
  CollectionEditor,
  decodeCollection,
  photoValues,
} from './collection-editor';
import updateContract from './outlet-update-contract.json';
import contract from './outlet-list-contract.json';

const updateKeys: Record<string, string> = {
  status: 'status_id',
  segment: 'segment_id',
  division: 'division_id',
  room_type: 'room_type_id',
  payment_type: 'payment_type_id',
  sub_channel: 'sub_channel_id',
  price_list: 'price_id',
};
const lookupFields = Object.keys(updateKeys);
const addressKeys = {
  address: 'full_address',
  region: 'region',
  city: 'city',
  address_id: 'id',
  coordinates: 'latlng',
};
const schemaFor = (id: string) => updateContract.fields[updateKeys[id] || id];
const editableFields = Object.keys(updateContract.fields)
  .filter(
    (id) =>
      ![
        'id',
        'photos',
        'route_number_id',
        ...Object.values(updateKeys),
      ].includes(id)
  )
  .concat(lookupFields, Object.keys(addressKeys), ['photos', 'route_numbers']);
const booleanOptions = [
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' },
];
function validateField(id: string, value: any): string | undefined {
  const schema = schemaFor(id);
  if (
    updateContract.required.includes(updateKeys[id] || id) &&
    (value == null || !String(value).trim())
  ) {
    return 'Обязательное поле';
  }
  if (
    schema?.type?.startsWith('integer') &&
    value != null &&
    !Number.isSafeInteger(Number(value))
  ) {
    return 'Укажите целое число';
  }
  if (schema?.type === 'boolean' && typeof value !== 'boolean') {
    return 'Выберите Да или Нет';
  }
  if (
    schema?.format === 'date' &&
    value &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ||
      !Number.isFinite(Date.parse(String(value))) ||
      new Date(String(value)).toISOString().slice(0, 10) !== value)
  ) {
    return 'Укажите корректную дату';
  }
  if (
    id === 'email' &&
    value &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))
  ) {
    return 'Укажите корректный email';
  }
  if (id === 'coordinates' && value) {
    const parts = String(value)
      .split(',')
      .map((v) => v.trim());
    if (
      parts.length !== 2 ||
      parts.some((v) => !v || !Number.isFinite(Number(v))) ||
      Math.abs(Number(parts[0])) > 90 ||
      Math.abs(Number(parts[1])) > 180
    ) {
      return 'Укажите широту и долготу через запятую';
    }
  }
}
function fieldValue(row: MasterOutlet, id: string) {
  if (id in addressKeys) {
    if (id === 'coordinates') {
      return row.address?.latlng?.join(', ') || null;
    }
    if (id === 'address') {
      return row.address?.full_address || row.address?.address || null;
    }
    return row.address?.[addressKeys[id]] ?? null;
  }
  if (lookupFields.includes(id)) {
    return row[id]?.id == null ? null : String(row[id].id);
  }
  return row[id] ?? null;
}
const labels: Record<string, string> = {
  id: 'ID',
  parent_id: 'ID ТТ-предшественника',
  photos: 'Фотографии',
  outlet_acb_id: 'ID заявки АКБ',
  unique_hash: 'Уникальный код ТТ',
  sfa_id: 'SFA ID',
  dist_system_id: 'ID в системе дистрибьютора',
  legal_name: 'Юридическое наименование',
  legal_address: 'Юридический адрес',
  inn: 'ИНН',
  has_inn: 'Есть ИНН',
  ogrn: 'ОГРН',
  kpp: 'КПП',
  okpo: 'ОКПО',
  bic: 'БИК',
  corr_account: 'Корреспондентский счёт',
  account: 'Расчётный счёт',
  payment_deferment: 'Отсрочка платежа',
  name: 'Наименование',
  subnet: 'Подсеть',
  title: 'Вывеска',
  phone: 'Телефон',
  director: 'Директор',
  email: 'Email',
  contact_person_1: 'Контактное лицо 1',
  contact_person_2: 'Контактное лицо 2',
  visits_frequency: 'Частота посещений',
  visits_day: 'Дни посещений',
  equipment_doors_number: 'Количество дверей оборудования',
  status: 'Статус',
  payment_type: 'Тип оплаты',
  channel: 'Канал',
  sub_channel: 'Подканал',
  division: 'Дивизион',
  route_numbers: 'Маршруты',
  price_list: 'Прайс-лист',
  segment: 'Сегмент',
  address: 'Адрес',
  master_user: 'Создал',
  acb_user: 'Инициатор',
  acb_create_dt: 'Дата создания заявки АКБ',
  create_dt: 'Дата создания',
  update_dt: 'Дата обновления',
  open_date: 'Дата открытия',
  close_date: 'Дата закрытия',
  comment: 'Комментарий',
  is_closed_for_visit: 'Недоступно к посещению',
  room_type: 'Тип помещения',
  distributor: 'Дистрибьютор',
  region: 'Регион',
  city: 'Город',
  sales_outlet_id: 'ID ТТ в источнике продаж',
};
// Grid IDs remain stable for views saved before the Swagger expansion.
const primaryFilters: Record<string, string> = {
  status: 'status_id',
  payment_type: 'payment_type_id',
  channel: 'channel_id',
  sub_channel: 'sub_channel_id',
  division: 'division_id',
  distributor: 'distributor_id',
  route_numbers: 'route_number_id',
  price_list: 'price_list_id',
  segment: 'segment_id',
  address: 'full_address',
  room_type: 'room_type_id',
  create_dt: 'create_date_range',
  update_dt: 'update_date_range',
  open_date: 'open_date_range',
  close_date: 'close_date_range',
};
const entities = [
  'status',
  'payment_type',
  'channel',
  'sub_channel',
  'division',
  'distributor',
  'price_list',
  'segment',
  'room_type',
];
const sortKey = (id: string) =>
  ({ route_numbers: 'route_number', room_type: 'room_type_id' }[id] || id);
type OutletFilterDefinition = GridFilter & { apiKey: string };

export const outletFilters: OutletFilterDefinition[] = Object.entries(
  contract.filters
).map(([apiKey, schema]) => {
  const primaryColumn = Object.keys(primaryFilters).find(
    (id) => primaryFilters[id] === apiKey
  );
  // A lookup can be filtered both by IDs and by its display name.
  const byName = !!primaryFilters[apiKey];
  const id = primaryColumn || (byName ? `${apiKey}_text` : apiKey);
  const label = labels[primaryColumn || apiKey] || apiKey;
  return {
    id,
    apiKey,
    label: byName ? `${label}: название` : label,
    filterType:
      schema.type === 'array'
        ? 'items' in schema && schema.items === 'integer'
          ? 'ids'
          : 'date-range'
        : schema.type === 'boolean'
        ? 'boolean'
        : schema.type === 'integer'
        ? 'integer'
        : schema.type === 'integer, null'
        ? 'nullable-integer'
        : 'text',
  };
});

const integer = (value: string, label: string): number => {
  if (!/^-?\d+$/.test(value.trim()) || !Number.isSafeInteger(Number(value))) {
    throw new Error(`${label}: укажите целое число`);
  }
  return Number(value);
};

export function outletQuery(view: GridView, page: number): RequestPayload {
  const filter: Record<string, any> = {};
  let channelSubIds: number[] | undefined;
  for (const definition of outletFilters) {
    const value = view.filters[definition.id];
    if (value === undefined || value === '') {
      continue;
    }
    const { apiKey, label, filterType } = definition;
    if (definition.id === 'channel' && value.startsWith('group:')) {
      channelSubIds = Array.from(
        new Set(
          value.split(',').flatMap((group) => {
            if (!group.startsWith('group:')) {
              throw new Error('Выберите каналы заново');
            }
            return group
              .slice(6)
              .split('|')
              .map((id) => integer(id, label));
          })
        )
      );
      continue;
    }
    if (filterType === 'integer' || filterType === 'nullable-integer') {
      filter[apiKey] =
        value === 'null' && filterType === 'nullable-integer'
          ? null
          : integer(value, label);
    } else if (filterType === 'boolean') {
      if (!['true', 'false'].includes(value)) {
        throw new Error(`${label}: выберите Да или Нет`);
      }
      filter[apiKey] = value === 'true';
    } else if (filterType === 'ids') {
      filter[apiKey] = Array.from(
        new Set(value.split(',').map((v) => integer(v, label)))
      );
    } else if (filterType === 'date-range') {
      const dates = value.split(',');
      const validDate = (date: string) =>
        /^\d{4}-\d{2}-\d{2}$/.test(date) &&
        Number.isFinite(Date.parse(date)) &&
        new Date(date).toISOString().slice(0, 10) === date;
      if (
        dates.length !== 2 ||
        !dates.every(validDate) ||
        dates[0] > dates[1]
      ) {
        throw new Error(`${label}: укажите корректный диапазон от и до`);
      }
      filter[apiKey] = dates;
    } else {
      filter[apiKey] = value;
    }
  }
  if (channelSubIds) {
    const ids = filter.sub_channel_id
      ? channelSubIds.filter((id) => filter.sub_channel_id.includes(id))
      : channelSubIds;
    if (!ids.length) {
      throw new Error('Выбранные подканалы не входят в выбранные каналы');
    }
    filter.sub_channel_id = ids;
  }
  const key = sortKey(view.sort.column);
  return {
    limit: view.pageSize,
    offset: (page - 1) * view.pageSize,
    // The supplied OutletListPayload explicitly documents "- asc, + desc".
    sort: `${view.sort.direction === 'desc' ? '+' : '-'}${
      contract.sortKeys.includes(key) ? key : 'id'
    }`,
    filter,
  };
}

export function outletColumns(
  guides: Record<string, EntityBase[]>,
  remoteLookups: Record<string, GridLookup> = {}
): GridColumn<MasterOutlet>[] {
  const originalOrder = [
    'id',
    'name',
    'title',
    'legal_name',
    'inn',
    'address',
    'status',
    'segment',
    'division',
    'room_type',
    'subnet',
    'phone',
    'email',
    'contact_person_1',
    'contact_person_2',
    'payment_deferment',
    'comment',
    'sfa_id',
    'route_numbers',
    'create_dt',
  ];
  const shown = [
    'id',
    'name',
    'title',
    'legal_name',
    'inn',
    'address',
    'status',
    'segment',
    'division',
    'room_type',
    'subnet',
    'phone',
    'email',
    'comment',
  ];
  const ids = Array.from(
    new Set([
      ...originalOrder,
      ...contract.fields,
      'region',
      'city',
      'parent_id',
      'photos',
    ])
  );
  const columns: GridColumn<MasterOutlet>[] = ids.map((id) => {
    const filter = outletFilters.find((f) => f.id === id);
    const editableLookup = lookupFields.includes(id);
    return {
      id,
      label: labels[id] || id,
      hidden: !shown.includes(id),
      sortable: contract.sortKeys.includes(sortKey(id)),
      filterable: !!filter,
      lookup:
        (entities.includes(id) || id === 'route_numbers') && filter
          ? remoteLookups[id] || { unavailable: !guides[id] }
          : undefined,
      filterType: filter?.filterType,
      editable:
        editableFields.includes(id) &&
        (!editableLookup || !!guides[id]?.length),
      type: editableLookup
        ? 'select'
        : schemaFor(id)?.type === 'boolean'
        ? 'boolean'
        : schemaFor(id)?.format === 'date'
        ? 'date'
        : schemaFor(id)?.type?.startsWith('integer')
        ? 'number'
        : 'text',
      options:
        schemaFor(id)?.type === 'boolean'
          ? booleanOptions
          : guides[id]?.map((v) => ({ value: String(v.id), label: v.name })),
      extraFilters: outletFilters.filter(
        (f) =>
          f.id === `${id}_text` || (id === 'id' && f.id === 'sales_outlet_id')
      ),
      getValue: (row) => {
        if (id === 'address') {
          return row.address?.full_address || row.address?.address || null;
        }
        if (id === 'region' || id === 'city') {
          return row.address?.[id] ?? null;
        }
        if (id === 'master_user' || id === 'acb_user') {
          return row[id]?.full_name || row[id]?.username || null;
        }
        if (entities.includes(id)) {
          return editableLookup || id === 'status'
            ? row[id]?.id == null
              ? null
              : String(row[id].id)
            : row[id]?.name ?? null;
        }
        return row[id] ?? null;
      },
      format: ['has_inn', 'is_closed_for_visit'].includes(id)
        ? (value) => (value == null ? '' : value ? 'Да' : 'Нет')
        : undefined,
      validate: (value) =>
        ['photos', 'route_numbers', 'parent_id'].includes(id)
          ? decodeCollection(value)
            ? undefined
            : 'Используйте редактор ячейки'
          : validateField(id, value),
      ...(['photos', 'route_numbers', 'parent_id'].includes(id)
        ? {
            isOriginalValue: (value) => {
              const data = decodeCollection(value);
              return (
                !!data &&
                JSON.stringify(data.before) === JSON.stringify(data.after)
              );
            },
            renderEditor: (props) =>
              createElement(CollectionEditor, {
                ...props,
                id: Number(props.row.id),
                kind: id as 'photos' | 'route_numbers' | 'parent_id',
                lookup: remoteLookups.route_numbers,
              }),
            format: (value) => {
              const data = decodeCollection(value)?.after;
              if (data === undefined) {
                return id === 'photos'
                  ? 'Открыть фотографии'
                  : String(value ?? '');
              }
              return id === 'photos'
                ? `Фотографий: ${data.length}`
                : id === 'route_numbers'
                ? `Маршрутов: ${data.length}`
                : String(data ?? '');
            },
          }
        : {}),
    };
  });
  columns.push({
    id: 'address_id',
    label: 'ID адреса',
    editable: true,
    type: 'number',
    validate: (value) =>
      value != null && !Number.isSafeInteger(Number(value))
        ? 'Укажите целое число'
        : undefined,
    hidden: true,
    getValue: (row) => row.address?.id ?? null,
  });
  columns.push({
    id: 'coordinates',
    label: 'Координаты',
    editable: true,
    validate: (value) => validateField('coordinates', value),
    hidden: true,
    getValue: (row) => row.address?.latlng?.join(', ') || null,
  });
  for (const user of ['master_user', 'acb_user']) {
    for (const field of ['username', 'phone', 'email']) {
      columns.push({
        id: `${user}_${field}`,
        label: `${labels[user]}: ${
          { username: 'логин', phone: 'телефон', email: 'email' }[field]
        }`,
        hidden: true,
        getValue: (row) => row[user]?.[field] ?? null,
      });
    }
  }
  return columns;
}
// Isolated request type follows the supplied Swagger, including nullable IDs.
export type OutletGridUpdate = {
  id: number;
  legal_address: string;
  legal_name: string;
  title: string;
  unique_hash: string;
  status_id: number;
  segment_id: number;
  [key: string]: any;
};
export function buildOutletUpdate(
  fresh: MasterOutlet,
  draft: GridDraft<MasterOutlet>
): OutletGridUpdate {
  const collections: Record<string, any> = {};
  const next = {
    ...fresh,
    address: fresh.address ? { ...fresh.address } : undefined,
  };
  for (const [field, value] of Object.entries(draft.changes)) {
    if (!editableFields.includes(field)) {
      throw new Error(`Поле ${field} нельзя изменить в таблице`);
    }
    if (['photos', 'route_numbers', 'parent_id'].includes(field)) {
      const data = decodeCollection(value);
      if (!data) {
        throw new Error('Используйте редактор ячейки');
      }
      const current =
        field === 'photos'
          ? photoValues(fresh)
          : field === 'parent_id'
          ? fresh.parent_id ?? null
          : fresh['route_number_id'];
      if (
        JSON.stringify(current) !== JSON.stringify(data.before) &&
        JSON.stringify(current) !== JSON.stringify(data.after)
      ) {
        throw new Error(
          `Поле «${labels[field]}» изменено другим пользователем`
        );
      }
      if (
        field === 'parent_id' &&
        data.after != null &&
        (!Number.isSafeInteger(data.after) || data.after === fresh.id)
      ) {
        throw new Error('Некорректный ID предшественника');
      }
      collections[field === 'route_numbers' ? 'route_number_id' : field] =
        data.after;
      continue;
    }
    const error = validateField(field, value);
    if (error) {
      throw new Error(`${labels[field] || field}: ${error}`);
    }
    const before = fieldValue(draft.original, field);
    const current = fieldValue(fresh, field);
    if (
      String(current ?? '') !== String(before ?? '') &&
      String(current ?? '') !== String(value ?? '')
    ) {
      throw new Error(
        `Поле «${
          labels[field] || field
        }» изменено другим пользователем. Отмените изменения строки и обновите данные.`
      );
    }
    if (field in addressKeys) {
      next.address = {
        ...next.address,
        [addressKeys[field]]:
          field === 'coordinates'
            ? value
              ? String(value)
                  .split(',')
                  .map((v) => v.trim())
              : undefined
            : value,
      };
    } else {
      next[field] = lookupFields.includes(field)
        ? value == null || value === ''
          ? null
          : { id: Number(value) }
        : value;
    }
  }
  const payload: OutletGridUpdate = {
    id: fresh.id,
    legal_address: next.legal_address,
    legal_name: next.legal_name,
    title: next.title,
    unique_hash: next.unique_hash,
    status_id: Number(next.status?.id),
    segment_id: Number(next.segment?.id),
  };
  for (const id of Object.keys(updateContract.fields)) {
    if (['id', 'address', 'photos', 'route_number_id'].includes(id)) {
      continue;
    }
    const field = lookupFields.find((key) => updateKeys[key] === id) || id;
    let value = fieldValue(next, field);
    const schema = schemaFor(field);
    if (value == null || value === '') {
      value = schema.type.includes('null') ? null : value;
    } else if (schema.type.startsWith('integer')) {
      value = Number(value);
    } else if (schema.type.startsWith('string')) {
      value = String(value);
    }
    // Never invent values for fields omitted by the detail endpoint.
    if (value != null || field in next || lookupFields.includes(field)) {
      payload[id] = value;
    }
  }
  for (const key of updateContract.required) {
    if (
      payload[key] == null ||
      payload[key] === '' ||
      (key.endsWith('_id') && !Number.isSafeInteger(payload[key]))
    ) {
      throw new Error(`Не заполнено обязательное поле: ${key}`);
    }
  }
  if (next.address) {
    payload.address = {
      id: next.address.id ?? null,
      region: next.address.region ?? null,
      city: next.address.city ?? null,
      full_address: next.address.full_address ?? next.address.address ?? null,
      ...(next.address.latlng
        ? { latlng: next.address.latlng.map(String) }
        : {}),
    };
  }
  if (next.images) {
    payload.photos = next.images.map((image) => ({
      image_pid: image.image_pid,
      filename: '',
      content: '',
    }));
  }
  return { ...payload, ...collections };
}
